/**
 * Dialog para completar una tarea de mantenimiento
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompleteMaintenance } from '@/hooks/usePlotterMaintenance';
import {
  completeMaintenanceSchema,
  type CompleteMaintenanceFormData,
} from '@/schemas/plotterMaintenanceSchemas';
import type { PlotterMaintenance } from '@/types/plotterMaintenance';
import { TASK_TYPE_CONFIG, STATUS_CONFIG } from '@/types/plotterMaintenance';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface CompleteMaintenanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: PlotterMaintenance | null;
}

export function CompleteMaintenanceDialog({
  open,
  onOpenChange,
  task,
}: CompleteMaintenanceDialogProps) {
  const { user } = useAuth();
  const completeMutation = useCompleteMaintenance();
  const isLoading = completeMutation.isPending;

  const form = useForm<CompleteMaintenanceFormData>({
    resolver: zodResolver(completeMaintenanceSchema),
    defaultValues: {
      actual_duration_minutes: 0,
      operating_hours_at_service: null,
      parts_replaced: [],
      consumables_used: [],
      labor_cost: null,
      parts_cost: null,
      notes: '',
      checklist: [],
    },
  });

  // Reset form cuando cambia la tarea
  useEffect(() => {
    if (task) {
      form.reset({
        actual_duration_minutes: task.estimated_duration_minutes,
        operating_hours_at_service: null,
        parts_replaced: [],
        consumables_used: [],
        labor_cost: null,
        parts_cost: null,
        notes: '',
        checklist: task.checklist || [],
      });
    }
  }, [task, form]);

  const checklist = form.watch('checklist');

  const toggleChecklistItem = (index: number) => {
    const current = form.getValues('checklist');
    const updated = [...current];
    updated[index] = { ...updated[index], completed: !updated[index].completed };
    form.setValue('checklist', updated);
  };

  const onSubmit = async (data: CompleteMaintenanceFormData) => {
    if (!task) return;

    try {
      await completeMutation.mutateAsync({
        id: task.id,
        data,
        performedBy: user?.id,
      });
      onOpenChange(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  if (!task) return null;

  const taskConfig = TASK_TYPE_CONFIG[task.task_type];
  const completedCount = checklist.filter(c => c.completed).length;
  const totalCount = checklist.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Completar Mantenimiento
          </DialogTitle>
        </DialogHeader>

        {/* Info de la tarea */}
        <div className="p-4 bg-muted/50 rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{task.title}</h3>
            <Badge className={cn(taskConfig.bgColor, taskConfig.color)}>
              {taskConfig.label}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Programado: {format(new Date(task.scheduled_date), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Checklist */}
            {checklist.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <FormLabel>Checklist ({completedCount}/{totalCount})</FormLabel>
                  <div className="h-2 w-24 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 transition-all"
                      style={{ width: totalCount > 0 ? `${(completedCount / totalCount) * 100}%` : '0%' }}
                    />
                  </div>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {checklist.map((item, index) => (
                    <div
                      key={index}
                      className={cn(
                        'flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors',
                        item.completed ? 'bg-green-500/10 border-green-500/30' : 'hover:bg-muted'
                      )}
                      onClick={() => toggleChecklistItem(index)}
                    >
                      <Checkbox
                        checked={item.completed}
                        onCheckedChange={() => toggleChecklistItem(index)}
                      />
                      <span className={cn(
                        'text-sm flex-1',
                        item.completed && 'line-through text-muted-foreground'
                      )}>
                        {item.task}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Duración real */}
              <FormField
                control={form.control}
                name="actual_duration_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duración Real (min) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Horas de operación */}
              <FormField
                control={form.control}
                name="operating_hours_at_service"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Horas de Operación</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        placeholder="Opcional"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Coste mano de obra */}
              <FormField
                control={form.control}
                name="labor_cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Coste Mano de Obra (€)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="0.00"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Coste piezas */}
              <FormField
                control={form.control}
                name="parts_cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Coste Piezas (€)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="0.00"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Notas */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas del Mantenimiento</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Observaciones, problemas encontrados, recomendaciones..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <CheckCircle className="w-4 h-4 mr-2" />
                Completar Mantenimiento
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
